import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message, Section, SendMessageRequest } from '@volt/shared';
import { ApiError } from '../api/client.js';
import { reportClientError } from '../lib/errorReporter.js';
import { parseChatEvent } from '../lib/sse.js';
import { cacheMessages, readCachedMessages } from '../lib/db.js';
import {
  EMPTY_REASONING,
  appendReasoning,
  pruneReasoning,
  type ReasoningByMessageId,
} from '../lib/reasoningStream.js';
import { useSession } from '../state/session.js';
import { useAnalyticsTracking } from '../state/analytics.js';
import { revealText } from './useTypewriter.js';
import { useOnline } from './useOnline.js';

export interface UseChatOptions {
  conversationId: string | null;
  section: Section;
  projectId?: string | null;
  onConversationCreated?: (id: string) => void;
  onError?: (code: string) => void;
}

export interface SendInput {
  text: string;
  modelId: string;
  attachmentIds?: string[];
  webSearch?: boolean;
  /** Live voice conversation: short spoken-style reply. */
  voice?: boolean;
}

export interface UseChat {
  messages: Message[];
  loading: boolean;
  streaming: boolean;
  thinking: boolean;
  /** Live processing explanations, keyed by assistant message id. */
  reasoningByMessageId: ReasoningByMessageId;
  send: (input: SendInput) => void;
  regenerate: (assistantMessageId: string, modelId: string) => void;
  editMessage: (userMessageId: string, text: string, modelId?: string) => Promise<void>;
  /** Pin / unpin a message as a chapter (synced to the server). */
  setChapter: (messageId: string, chapter: boolean) => void;
  /** Delete this message and everything after it. */
  rewindTo: (messageId: string) => Promise<void>;
  /** A message that could not be sent (network down / stream failed to open). */
  failedSendId: string | null;
  retrySend: () => void;
  stop: () => void;
  setFeedback: (messageId: string, feedback: 'up' | 'down' | null) => void;
}

function newLocalId(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function newClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${newLocalId()}`;
}

function baseMessage(
  id: string,
  conversationId: string,
  role: Message['role'],
  content: string,
  modelId: string | null,
  status: Message['status'],
): Message {
  return {
    id,
    conversationId,
    userId: '',
    role,
    content,
    modelId,
    parentId: null,
    inputTokens: 0,
    outputTokens: 0,
    costKopecks: 0,
    status,
    feedback: null,
    attachments: [],
    generationMs: null,
    isChapter: false,
    createdAt: new Date().toISOString(),
  };
}

function errorCode(error: unknown): string {
  if (error instanceof ApiError) return error.code;
  return 'generic';
}

export async function persistOwnedMessageEdit(
  messages: Message[],
  userId: string | undefined,
  messageId: string,
  text: string,
  save: (messageId: string, content: string) => Promise<{ message: Message }>,
): Promise<Message> {
  const message = messages.find((item) => item.id === messageId);
  if (!message || message.role !== 'user' || message.userId !== userId ||
      message.id.startsWith('local-') || !text.trim()) throw new Error('Message cannot be edited');
  const { message: updated } = await save(messageId, text.trim());
  return updated;
}

/**
 * Owns the message list for the active conversation: loads history (with offline
 * cache fallback), streams assistant replies token-by-token over SSE, and
 * supports stop / regenerate / persistent text editing / feedback.
 */
export function useChat(options: UseChatOptions): UseChat {
  const { conversationId, section, projectId, onConversationCreated, onError } = options;
  const { api, data, setBalance } = useSession();
  const analytics = useAnalyticsTracking();

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [reasoningByMessageId, setReasoningByMessageId] =
    useState<ReasoningByMessageId>(EMPTY_REASONING);

  const [failedSend, setFailedSend] = useState<{ optimisticId: string; body: SendMessageRequest } | null>(null);
  const online = useOnline();
  const messagesRef = useRef<Message[]>([]);
  messagesRef.current = messages;
  const abortRef = useRef<AbortController | null>(null);
  const assistantIdRef = useRef<string | null>(null);
  const createdIdRef = useRef<string | null>(null);
  const activeConversationRef = useRef(conversationId);
  const conversationVersionRef = useRef(0);
  if (activeConversationRef.current !== conversationId) conversationVersionRef.current += 1;
  activeConversationRef.current = conversationId;
  const mountedRef = useRef(true);
  const editPendingRef = useRef<number | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!conversationId) {
      setMessages([]);
      setReasoningByMessageId(EMPTY_REASONING);
      return;
    }
    if (conversationId === createdIdRef.current) {
      // Conversation was just created by an in-flight stream; keep local state.
      return;
    }
    setReasoningByMessageId(EMPTY_REASONING);
    setLoading(true);
    (async () => {
      try {
        const response = await api.getConversation(conversationId);
        if (cancelled) return;
        setMessages(response.messages);
        void cacheMessages(response.messages);
      } catch {
        const cached = await readCachedMessages(conversationId);
        if (!cancelled) setMessages(cached);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, conversationId]);

  const patchMessage = useCallback((id: string, patch: Partial<Message>) => {
    setMessages((prev) => prev.map((message) => (message.id === id ? { ...message, ...patch } : message)));
  }, []);

  // Explanations live only as long as the message they belong to: a new turn,
  // regenerate or conversation switch drops the messages and with them
  // any explanation, so text can never leak into another message or chat.
  useEffect(() => {
    setReasoningByMessageId((prev) =>
      pruneReasoning(
        prev,
        messages.map((message) => message.id),
      ),
    );
  }, [messages]);

  // Ref that tracks the cancel function for any active typewriter animation so
  // we can stop it if recovery finishes, the component unmounts, or a new stream
  // starts before the previous one settled.
  const cancelTypewriterRef = useRef<(() => void) | null>(null);
  // Ref that tracks the cancel function for an active recovery poll loop.
  const cancelPollRef = useRef<(() => void) | null>(null);

  // Cancel in-flight poll/typewriter on unmount to avoid state-after-unmount.
  useEffect(() => {
    return () => {
      cancelPollRef.current?.();
      cancelTypewriterRef.current?.();
    };
  }, []);

  /**
   * Robust recovery: after an unexpected stream end/error, poll
   * `getConversation` until the server message reaches a terminal state
   * (complete / failed / cancelled) or until the timeout elapses.
   *
   * While waiting, the hook stays in `thinking` state so the UI shows a
   * typing indicator rather than an error.  Any partial content that arrives
   * in intermediate polls is fed through the typewriter so the recovery UX
   * feels like live streaming.
   *
   * @param id       The assistant message id to track.
   * @param convId   The conversation id to poll.
   * @param aborted  Whether the user explicitly aborted — if so, skip recovery.
   */
  const recoverAssistantMessage = useCallback(
    (id: string, convId: string, aborted: boolean) => {
      if (aborted) return;

      // Cancel any prior poll/typewriter that might still be running.
      cancelPollRef.current?.();
      cancelTypewriterRef.current?.();
      cancelTypewriterRef.current = null;

      let stopped = false;
      let revealedChars = 0; // how many chars have been fed to the typewriter so far
      const POLL_INTERVAL_MS = 1500;
      const TIMEOUT_MS = 60_000;
      const startedAt = Date.now();

      function failRecovery(code: string) {
        if (stopped) return;
        stopped = true;
        cancelTypewriterRef.current?.();
        cancelTypewriterRef.current = null;
        cancelPollRef.current = null;
        patchMessage(id, { status: 'failed' });
        onError?.(code);
        setStreaming(false);
        setThinking(false);
        void cacheMessages(messagesRef.current);
      }

      function cancel() {
        stopped = true;
        cancelTypewriterRef.current?.();
        cancelTypewriterRef.current = null;
      }
      cancelPollRef.current = cancel;

      // Keep thinking=true / streaming=true so the UI shows the typing indicator.
      setThinking(true);
      setStreaming(true);

      async function poll() {
        if (stopped) return;

        try {
          const recovered = await api.getConversation(convId);
          if (stopped) return;
          const serverMsg = recovered.messages.find((m) => m.id === id);

          if (serverMsg) {
            const newContent = serverMsg.content ?? '';

            // Feed any newly-arrived characters through the typewriter so the
            // user sees content appearing progressively even before completion.
            if (newContent.length > revealedChars) {
              cancelTypewriterRef.current?.();
              const fromChar = revealedChars;
              cancelTypewriterRef.current = revealText(newContent, fromChar, (partial) => {
                if (!stopped) patchMessage(id, { content: partial });
              }, () => {
                cancelTypewriterRef.current = null;
              });
              revealedChars = newContent.length;
            }

            const terminal = serverMsg.status !== 'streaming';
            if (terminal) {
              // Wait for the typewriter to finish before finalising the message
              // so the last few characters don't vanish/jump.
              const finalize = () => {
                if (stopped) return;
                stopped = true;
                cancelPollRef.current = null;
                patchMessage(id, {
                  content: newContent,
                  status: serverMsg.status === 'complete' ? 'complete' : 'failed',
                  inputTokens: serverMsg.inputTokens,
                  outputTokens: serverMsg.outputTokens,
                  costKopecks: serverMsg.costKopecks,
                });
                if (serverMsg.status !== 'complete') {
                  onError?.('streamInterrupted');
                }
                setStreaming(false);
                setThinking(false);
                void cacheMessages(messagesRef.current);
              };

              // If typewriter is still running, let it finish then finalize.
              // We schedule finalize via a short poll rather than coupling into
              // revealText's internals.
              const waitForTypewriter = () => {
                if (stopped) return;
                if (cancelTypewriterRef.current === null) {
                  finalize();
                } else {
                  setTimeout(waitForTypewriter, 50);
                }
              };
              waitForTypewriter();
              return;
            }
          }
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            failRecovery(error.code);
            return;
          }
          // Network hiccup — keep polling until timeout.
        }

        if (Date.now() - startedAt >= TIMEOUT_MS) {
          failRecovery('streamInterrupted');
          return;
        }

        setTimeout(() => { void poll(); }, POLL_INTERVAL_MS);
      }

      setTimeout(() => { void poll(); }, POLL_INTERVAL_MS);
    },
    [api, onError, patchMessage],
  );

  const recoverAssistantByRequestId = useCallback(
    async (
      clientRequestId: string,
      optimisticUserId: string | null,
      aborted: boolean,
    ): Promise<{ recovered: boolean; errorCode?: string }> => {
      if (aborted) return { recovered: false };
      const retryDelaysMs = [0, 300, 700, 1300, 2200, 3200];
      for (const delayMs of retryDelaysMs) {
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          if (aborted) return { recovered: false };
        }
        try {
          const recovered = await api.getMessageByRequest(clientRequestId);
          if (aborted) return { recovered: false };
          const convId = recovered.conversationId;
          const assistant = recovered.message;
          assistantIdRef.current = assistant.id;
          if (!conversationId) {
            createdIdRef.current = convId;
            onConversationCreated?.(convId);
          }
          setMessages((prev) => {
            let next = prev;
            if (optimisticUserId) {
              next = next.map((message) =>
                message.id === optimisticUserId
                  ? {
                      ...message,
                      id: assistant.parentId ?? message.id,
                      conversationId: convId,
                    }
                  : message,
              );
            }
            const idx = next.findIndex((message) => message.id === assistant.id);
            if (idx === -1) {
              return [...next, assistant];
            }
            return next.map((message, i) => (i === idx ? { ...message, ...assistant } : message));
          });
          recoverAssistantMessage(assistant.id, convId, false);
          return { recovered: true };
        } catch (error) {
          if (error instanceof ApiError && error.status === 404) {
            continue;
          }
          if (error instanceof ApiError) {
            return { recovered: false, errorCode: error.code };
          }
          return { recovered: false };
        }
      }
      return { recovered: false };
    },
    [api, conversationId, onConversationCreated, recoverAssistantMessage],
  );

  const runStream = useCallback(
    async (body: SendMessageRequest, optimisticUserId: string | null) => {
      // Cancel any in-progress recovery poll/typewriter from a previous run.
      cancelPollRef.current?.();
      cancelPollRef.current = null;
      cancelTypewriterRef.current?.();
      cancelTypewriterRef.current = null;

      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      setThinking(true);
      let recoveryStarted = false;
      try {
        const stream = await api.openChatStream(body, controller.signal);
        for await (const sseEvent of stream) {
          const event = parseChatEvent(sseEvent.data);
          if (!event) continue;
          switch (event.type) {
            case 'start': {
              assistantIdRef.current = event.assistantMessageId;
              if (!conversationId) {
                createdIdRef.current = event.conversationId;
                onConversationCreated?.(event.conversationId);
              }
              setMessages((prev) => {
                const next = prev.map((message) =>
                  message.id === optimisticUserId
                    ? { ...message, id: event.userMessageId, conversationId: event.conversationId }
                    : message,
                );
                next.push(
                  baseMessage(
                    event.assistantMessageId,
                    event.conversationId,
                    'assistant',
                    '',
                    body.modelId,
                    'streaming',
                  ),
                );
                return next;
              });
              setBalance(event.balanceKopecks);
              break;
            }
            case 'thinking': {
              const id = assistantIdRef.current;
              if (id) {
                setReasoningByMessageId((prev) => appendReasoning(prev, id, event.text));
              }
              break;
            }
            case 'delta': {
              setThinking(false);
              const id = assistantIdRef.current;
              if (id) {
                setMessages((prev) =>
                  prev.map((message) =>
                    message.id === id ? { ...message, content: message.content + event.text } : message,
                  ),
                );
              }
              break;
            }
            case 'usage': {
              const id = assistantIdRef.current;
              if (id) {
                patchMessage(id, {
                  inputTokens: event.inputTokens,
                  outputTokens: event.outputTokens,
                  costKopecks: event.costKopecks,
                  generationMs: event.generationMs,
                });
              }
              setBalance(event.balanceKopecks);
              break;
            }
            case 'done': {
              const id = assistantIdRef.current;
              if (id) patchMessage(id, { status: 'complete' });
              break;
            }
            case 'error': {
              const id = assistantIdRef.current;
              if (id) patchMessage(id, { status: 'failed', errorMessage: event.message });
              onError?.(event.code);
              break;
            }
            default:
              break;
          }
        }
        // Stream ended normally — if the assistant message is still 'streaming'
        // (done event was lost due to proxy buffering or premature EOF), start
        // a polling recovery loop rather than a single fetch.
        const id = assistantIdRef.current;
        const convId = createdIdRef.current ?? conversationId;
        if (id && convId) {
          const stillStreaming = messagesRef.current.find((m) => m.id === id)?.status === 'streaming';
          if (stillStreaming) {
            // recoverAssistantMessage takes over streaming/thinking state.
            recoveryStarted = true;
            recoverAssistantMessage(id, convId, controller.signal.aborted);
          }
        } else if (!controller.signal.aborted && body.clientRequestId) {
          const recovery = await recoverAssistantByRequestId(
            body.clientRequestId,
            optimisticUserId,
            controller.signal.aborted,
          );
          recoveryStarted = recovery.recovered;
          if (!recoveryStarted) onError?.(recovery.errorCode ?? 'streamInterrupted');
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          const id = assistantIdRef.current;
          const convId = createdIdRef.current ?? conversationId;
          if (id && convId) {
            // Stream died after the `start` event — start a polling recovery loop.
            recoveryStarted = true;
            recoverAssistantMessage(id, convId, false);
          } else {
            let recoveryErrorCode: string | undefined;
            if (body.clientRequestId) {
              const recovery = await recoverAssistantByRequestId(
                body.clientRequestId,
                optimisticUserId,
                false,
              );
              recoveryStarted = recovery.recovered;
              recoveryErrorCode = recovery.errorCode;
            }
            if (recoveryStarted) return;
            if (id) patchMessage(id, { status: 'failed' });
            else if (optimisticUserId) setFailedSend({ optimisticId: optimisticUserId, body });
            reportClientError({
              code: 'CHAT_STREAM_TRANSPORT',
              message:
                error instanceof ApiError
                  ? `${error.code}:${error.message || 'stream failed'}`
                  : ((error as Error)?.message ?? 'Chat stream transport failed'),
              route: '/chat/messages',
              metadata: {
                stage: 'stream-open',
                modelId: body.modelId,
                conversationId: body.conversationId ?? null,
                clientRequestId: body.clientRequestId ?? null,
                errorCode: error instanceof ApiError ? error.code : null,
                status: error instanceof ApiError ? error.status : null,
              },
            });
            onError?.(recoveryErrorCode ?? errorCode(error));
          }
        }
      } finally {
        abortRef.current = null;
        assistantIdRef.current = null;
        if (!recoveryStarted) {
          // Recovery owns streaming/thinking/cache when it has taken over.
          setStreaming(false);
          setThinking(false);
          void cacheMessages(messagesRef.current);
        }
      }
    },
    [
      api,
      conversationId,
      onConversationCreated,
      onError,
      patchMessage,
      recoverAssistantByRequestId,
      recoverAssistantMessage,
      setBalance,
    ],
  );

  const send = useCallback(
    ({ text, modelId, attachmentIds, webSearch, voice }: SendInput) => {
      if (streaming) return;
      analytics.trackAiRequest();
      const optimisticId = newLocalId();
      const user = baseMessage(
        optimisticId,
        conversationId ?? 'pending',
        'user',
        text,
        modelId,
        'complete',
      );
      setMessages((prev) => [...prev, user]);
      const body: SendMessageRequest = {
        modelId,
        text,
        clientRequestId: newClientRequestId(),
        conversationId: conversationId ?? undefined,
        section,
        projectId: projectId ?? undefined,
        attachmentIds,
        webSearch,
        voice: voice || undefined,
      };
      void runStream(body, optimisticId);
    },
    [analytics, streaming, conversationId, section, projectId, runStream],
  );

  const retrySend = useCallback(() => {
    if (!failedSend || streaming) return;
    const { optimisticId, body } = failedSend;
    setFailedSend(null);
    // Same clientRequestId: the server treats a repeated request idempotently.
    void runStream(body, optimisticId);
  }, [failedSend, streaming, runStream]);

  // Retry automatically when the connection comes back (offline → online only,
  // so a server-side failure can never turn into a retry loop).
  const prevOnlineRef = useRef(online);
  useEffect(() => {
    const cameBack = online && !prevOnlineRef.current;
    prevOnlineRef.current = online;
    if (cameBack && failedSend && !streaming) retrySend();
  }, [online, failedSend, streaming, retrySend]);

  useEffect(() => { setFailedSend(null); }, [conversationId]);

  const regenerate = useCallback(
    (assistantMessageId: string, modelId: string) => {
      if (streaming) return;
      analytics.trackAiRequest();
      const current = messagesRef.current;
      const index = current.findIndex((message) => message.id === assistantMessageId);
      if (index === -1) return;
      const priorUser = [...current.slice(0, index)].reverse().find((m) => m.role === 'user');
      setMessages(current.slice(0, index));
      const body: SendMessageRequest = {
        modelId,
        text: priorUser?.content ?? '',
        clientRequestId: newClientRequestId(),
        conversationId: conversationId ?? undefined,
        regenerateOfMessageId: assistantMessageId,
      };
      void runStream(body, null);
    },
    [analytics, streaming, conversationId, runStream],
  );

  const editMessage = useCallback(
    async (userMessageId: string, text: string) => {
      const version = conversationVersionRef.current;
      if (streaming || editPendingRef.current === version || !conversationId ||
          !messagesRef.current.some((message) =>
            message.id === userMessageId && message.conversationId === conversationId)) {
        throw new Error('Message cannot be edited');
      }
      editPendingRef.current = version;
      try {
        const updated = await persistOwnedMessageEdit(
          messagesRef.current, data?.user.id, userMessageId, text,
          (id, content) => api.editConversationMessage(conversationId, id, content),
        );
        if (!mountedRef.current || version !== conversationVersionRef.current) return;
        const next = messagesRef.current.map((item) => item.id === userMessageId ? updated : item);
        messagesRef.current = next;
        setMessages(next);
        void cacheMessages(next);
      } finally {
        if (editPendingRef.current === version) editPendingRef.current = null;
      }
    },
    [api, data?.user.id, streaming, conversationId],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    const id = assistantIdRef.current;
    if (id) {
      patchMessage(id, { status: 'cancelled' });
      void api.stopGeneration(id).catch(() => undefined);
    }
    setStreaming(false);
    setThinking(false);
  }, [api, patchMessage]);

  const setFeedback = useCallback(
    (messageId: string, feedback: 'up' | 'down' | null) => {
      patchMessage(messageId, { feedback });
      void api.setFeedback(messageId, feedback).catch(() => undefined);
    },
    [api, patchMessage],
  );

  const rewindTo = useCallback(
    async (messageId: string) => {
      const current = messagesRef.current;
      const index = current.findIndex((m) => m.id === messageId && m.conversationId === conversationId);
      if (streaming || !conversationId || index < 0 || messageId.startsWith('local-')) {
        throw new Error('Cannot return to this message');
      }
      const version = conversationVersionRef.current;
      await api.rewindConversation(conversationId, messageId);
      if (!mountedRef.current || version !== conversationVersionRef.current) return;
      const next = messagesRef.current.slice(0, messagesRef.current.findIndex((m) => m.id === messageId));
      messagesRef.current = next;
      setMessages(next);
      void cacheMessages(next);
    },
    [api, streaming, conversationId],
  );

  const setChapter = useCallback(
    (messageId: string, chapter: boolean) => {
      if (!conversationId || messageId.startsWith('local-')) return;
      patchMessage(messageId, { isChapter: chapter });
      api.setMessageChapter(conversationId, messageId, chapter).then(
        () => void cacheMessages(messagesRef.current),
        () => patchMessage(messageId, { isChapter: !chapter }),
      );
    },
    [api, conversationId, patchMessage],
  );

  return {
    messages,
    loading,
    streaming,
    thinking,
    reasoningByMessageId,
    send,
    regenerate,
    editMessage,
    rewindTo,
    setChapter,
    failedSendId: failedSend?.optimisticId ?? null,
    retrySend,
    stop,
    setFeedback,
  };
}
